import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BusinessConfigService, CancellationPolicyResponse, RefundFeeType } from '../../services/business-config.service';
import { NotificationService } from '../../services/notification.service';

const formatHours = (hours: number) =>
  hours >= 24 && hours % 24 === 0 ? `${hours / 24} ${hours === 24 ? 'día' : 'días'}` : `${hours} ${hours === 1 ? 'hora' : 'horas'}`;

/**
 * Política de cancelación del negocio: cada negocio decide con cuánta
 * anticipación sus clientes pueden cancelar (antes eran 6 h fijas para todo
 * el sistema) y si descuenta un cargo administrativo de los reembolsos. Lo
 * elegido se muestra tal cual en la ficha pública de cada espacio, y Kubo
 * lo recuerda mientras no se haya configurado.
 */
@Component({
  selector: 'app-cancellation-policy-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="cp-card" aria-labelledby="cp-title">
      <header class="cp-head">
        <div>
          <h3 id="cp-title"><i class="bi bi-calendar-x" aria-hidden="true"></i> Cancelaciones y reembolsos</h3>
          <p>Define cuándo pueden cancelar tus clientes y si aplicas un cargo administrativo a los reembolsos. Se muestra en la ficha de cada espacio.</p>
        </div>
        <span class="cp-status" [class.pending]="!configured()">{{ configured() ? 'Configurada' : 'Pendiente de configurar' }}</span>
      </header>

      <p class="cp-state" *ngIf="loading()">Cargando política…</p>
      <p class="cp-state cp-error" *ngIf="loadError()">{{ loadError() }}</p>

      <ng-container *ngIf="!loading() && !loadError()">
        <p class="cp-pending" *ngIf="!configured()"><i class="bi bi-info-circle" aria-hidden="true"></i> Mientras no la configures, tus clientes pueden cancelar hasta {{ formatHours(defaultHours) }} antes y no se aplica ningún cargo.</p>

        <fieldset class="cp-group">
          <legend>Anticipación mínima para cancelar</legend>
          <div class="cp-options" role="radiogroup">
            <label *ngFor="let h of hourOptions" [class.on]="hours === h">
              <input type="radio" name="cp-hours" [value]="h" [(ngModel)]="hours"> {{ formatHours(h) }}
            </label>
          </div>
          <small>Después de ese límite, la reserva ya no se puede cancelar desde la cuenta del cliente.</small>
        </fieldset>

        <fieldset class="cp-group">
          <legend>Cargo administrativo sobre reembolsos</legend>
          <div class="cp-options" role="radiogroup">
            <label [class.on]="feeType === 'none'"><input type="radio" name="cp-fee" value="none" [(ngModel)]="feeType"> Sin cargo</label>
            <label [class.on]="feeType === 'percent'"><input type="radio" name="cp-fee" value="percent" [(ngModel)]="feeType"> Porcentaje</label>
            <label [class.on]="feeType === 'fixed'"><input type="radio" name="cp-fee" value="fixed" [(ngModel)]="feeType"> Monto fijo</label>
          </div>
          <div class="cp-value" *ngIf="feeType !== 'none'">
            <span>{{ feeType === 'fixed' ? '$' : '' }}</span>
            <input placeholder="{{ feeType === 'percent' ? 'Ej: 25' : 'Ej: 10.00' }}" type="number" [min]="0.01" [max]="feeType === 'percent' ? maxPercent : maxFixed" step="0.01" [(ngModel)]="feeValue" aria-label="Valor del cargo administrativo" data-no-limit-hint>
            <span>{{ feeType === 'percent' ? '% del total pagado' : 'por reembolso' }}</span>
          </div>
          <small>Se descuenta del monto devuelto. Nunca supera el total pagado.</small>
        </fieldset>

        <div class="cp-preview" aria-live="polite">
          <strong>Así lo verá el cliente</strong>
          <span>{{ previewText() }}</span>
          <span class="cp-example" *ngIf="feeType !== 'none' && feeValue > 0">Ejemplo: en una reserva de $40.00 el reembolso sería de \${{ exampleRefund() | number:'1.2-2' }}.</span>
        </div>

        <p class="cp-error" *ngIf="formError()" role="alert">{{ formError() }}</p>
        <footer>
          <button type="button" class="cp-save" (click)="save()" [disabled]="saving()">{{ saving() ? 'Guardando…' : 'Guardar política' }}</button>
        </footer>
      </ng-container>
    </section>
  `,
  styles: [`
    :host { display: block; margin-bottom: 1.4rem; }
    .cp-card { padding: 1.2rem; border-radius: 14px; border: 1px solid var(--ts-border-strong); background: var(--ts-surface); }
    .cp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: .8rem; }
    .cp-head h3 { display: flex; align-items: center; gap: .5rem; margin: 0 0 .25rem; color: var(--ts-text); font-size: 1.02rem; }
    .cp-head h3 i, .cp-pending i { display: grid; place-items: center; flex: 0 0 1.25rem; width: 1.25rem; height: 1.25rem; line-height: 1; }
    .cp-head p { margin: 0; color: var(--ts-text-soft); font-size: .84rem; line-height: 1.5; }
    .cp-status { flex: none; padding: .2rem .65rem; border-radius: 999px; color: var(--ts-success-text, #7ee2b2); border: 1px solid currentColor; font-size: .72rem; font-weight: 750; }
    .cp-status.pending { color: var(--ts-warning-text); }
    .cp-state { color: var(--ts-text-muted); font-size: .86rem; }
    .cp-error { color: var(--ts-danger-text); font-size: .84rem; }
    .cp-pending { display: flex; gap: .5rem; margin: 0 0 .8rem; padding: .6rem .75rem; border-radius: 10px; color: var(--ts-warning-text); background: var(--ts-bg-amber-500-a8); font-size: .82rem; }
    .cp-group { margin: 0 0 .9rem; padding: 0; border: 0; }
    .cp-group legend { margin-bottom: .45rem; color: var(--ts-text); font-size: .86rem; font-weight: 750; }
    .cp-group small { display: block; margin-top: .4rem; color: var(--ts-text-muted); font-size: .76rem; }
    .cp-options { display: flex; flex-wrap: wrap; gap: .4rem; }
    .cp-options label { display: inline-grid; grid-template-columns: 1rem auto; align-items: center; justify-content: center; gap: .45rem; min-width: 112px; min-height: 40px; padding: .35rem .75rem; box-sizing: border-box; border: 1px solid var(--ts-border-strong); border-radius: 999px; color: var(--ts-text-soft); font-size: .82rem; font-weight: 650; line-height: 1.25; cursor: pointer; }
    .cp-options label.on { color: var(--ts-text); border-color: var(--ts-primary); background: var(--ts-bg-blue-400-a14); }
    .cp-options input { appearance: none; width: 1rem; height: 1rem; margin: 0; border: 1.5px solid #64748b; border-radius: 50%; background: #fff; }
    .cp-options input:checked { border-color: var(--ts-primary); background: radial-gradient(circle, var(--ts-primary) 0 42%, #fff 46% 100%); }
    .cp-options input:focus-visible { outline: 3px solid color-mix(in srgb, var(--ts-primary) 25%, transparent); outline-offset: 2px; }
    .cp-value { display: flex; align-items: center; gap: .5rem; margin-top: .6rem; color: var(--ts-text-soft); font-size: .84rem; }
    .cp-value input { width: 110px; min-height: 40px; padding: .4rem .6rem; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-input-bg); font: inherit; }
    .cp-preview { display: grid; gap: .25rem; padding: .75rem .9rem; border-radius: 11px; border: 1px dashed var(--ts-primary-border); background: var(--ts-bg-blue-400-a7); font-size: .84rem; }
    .cp-preview strong { color: var(--ts-text); }
    .cp-preview span { color: var(--ts-text-soft); line-height: 1.45; }
    .cp-example { color: var(--ts-text-muted) !important; font-size: .78rem; }
    footer { display: flex; justify-content: flex-end; margin-top: .9rem; }
    .cp-save { min-height: 40px; padding: 0 1rem; border: 0; border-radius: 10px; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); font: inherit; font-size: .86rem; font-weight: 750; cursor: pointer; }
    .cp-save:disabled { opacity: .55; cursor: not-allowed; }
    @media (max-width: 640px) { .cp-head { flex-direction: column; } }
  `],
})
export class CancellationPolicySettingsComponent implements OnInit {
  private readonly config = inject(BusinessConfigService);
  private readonly auth = inject(AuthService);
  private readonly notifications = inject(NotificationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly loadError = signal('');
  readonly formError = signal('');
  readonly configured = signal(false);

  hourOptions: number[] = [1, 2, 3, 6, 12, 24, 48, 72];
  defaultHours = 6;
  maxPercent = 50;
  maxFixed = 1000;
  hours = 6;
  feeType: RefundFeeType = 'none';
  feeValue = 0;
  readonly formatHours = formatHours;

  ngOnInit(): void { void this.load(); }

  private apply(response: CancellationPolicyResponse): void {
    this.hourOptions = response.options.hours;
    this.defaultHours = response.options.default_hours;
    this.maxPercent = response.options.max_percent;
    this.maxFixed = response.options.max_fixed;
    this.hours = response.policy.cancellation_min_hours;
    this.feeType = response.policy.refund_fee_type;
    this.feeValue = response.policy.refund_fee_value;
    this.configured.set(!!response.configured_at);
  }

  async load(): Promise<void> {
    const businessId = this.auth.currentUser()?.id;
    if (!businessId) { this.loading.set(false); this.loadError.set('No se pudo identificar el negocio.'); return; }
    try {
      this.apply(await firstValueFrom(this.config.obtenerPoliticaCancelacion(businessId)));
    } catch (error: any) {
      this.loadError.set(typeof error === 'string' ? error : 'No se pudo cargar la política de cancelación.');
    } finally {
      this.loading.set(false);
    }
  }

  previewText(): string {
    const base = `Puedes cancelar sin costo hasta ${formatHours(this.hours)} antes del inicio de tu reserva.`;
    if (this.feeType === 'none' || !(this.feeValue > 0)) return `${base} Los reembolsos no tienen cargo administrativo.`;
    const fee = this.feeType === 'percent' ? `${this.feeValue}% del total pagado` : `$${Number(this.feeValue).toFixed(2)}`;
    return `${base} Si solicitas un reembolso, se descuenta un cargo administrativo de ${fee}.`;
  }

  exampleRefund(): number {
    const total = 40;
    const fee = this.feeType === 'percent' ? total * (this.feeValue / 100) : this.feeType === 'fixed' ? this.feeValue : 0;
    return Math.max(total - Math.min(Math.max(fee, 0), total), 0);
  }

  async save(): Promise<void> {
    this.formError.set('');
    const value = this.feeType === 'none' ? 0 : Number(this.feeValue);
    if (this.feeType === 'percent' && !(value > 0 && value <= this.maxPercent)) { this.formError.set(`El porcentaje debe estar entre 0.01 y ${this.maxPercent}%.`); return; }
    if (this.feeType === 'fixed' && !(value > 0 && value <= this.maxFixed)) { this.formError.set(`El cargo fijo debe estar entre $0.01 y $${this.maxFixed}.`); return; }
    const businessId = this.auth.currentUser()?.id;
    if (!businessId) return;
    this.saving.set(true);
    try {
      this.apply(await firstValueFrom(this.config.guardarPoliticaCancelacion(businessId, { cancellation_min_hours: this.hours, refund_fee_type: this.feeType, refund_fee_value: value })));
      this.notifications.success('Política de cancelación guardada.');
      // Kubo (guía de configuración) vuelve a evaluar sus pasos.
      window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
    } catch (error: any) {
      this.formError.set(typeof error === 'string' ? error : 'No se pudo guardar la política.');
    } finally {
      this.saving.set(false);
    }
  }
}
